import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Eye, FileText, Send } from "lucide-react";
import type { DocumentListResponse, DocumentSummary } from "@pkhoffice/shared";
import { api } from "@/lib/api/client";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { RowActions } from "@/components/ui/RowActions";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/button";
import { EntityPickerField, type PickedEntity } from "@/components/ui/EntityPicker";
import { AttachmentCell, type AttachmentRef } from "@/components/ui/AttachmentCell";
import { FilePreviewModal } from "@/components/ui/FilePreviewModal";

type Filters = {
  q: string;
  fromDate: string;
  toDate: string;
  typeId: string;
};

const EMPTY_FILTERS: Filters = { q: "", fromDate: "", toDate: "", typeId: "" };

type Fetcher = (p: Record<string, string | number | undefined>) => Promise<DocumentListResponse>;

type Tab = { key: string; label: string; fetcher: Fetcher };

type Props = {
  /** Page title — e.g. "Văn bản đến" */
  title: string;
  /** Breadcrumb tail — e.g. "Quản lý Văn bản › Văn bản đến" */
  breadcrumb: string;
  /** React Query key prefix so each view has its own cache. */
  queryKey: string;
  /** Fetcher that hits the view-specific endpoint. Bỏ qua khi có `tabs`. */
  fetcher?: Fetcher;
  /**
   * Thanh tab trong màn, giống hệ cũ ("Văn bản đã nhận | Văn bản đã gửi").
   * Khi có tabs thì `fetcher` lấy theo tab đang chọn.
   */
  tabs?: Tab[];
};

export function DocumentViewListPage({ title, breadcrumb, queryKey, fetcher, tabs }: Props) {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [previewFile, setPreviewFile] = useState<AttachmentRef | null>(null);
  const [transferDoc, setTransferDoc] = useState<DocumentSummary | null>(null);
  const qc = useQueryClient();
  const markMut = useMutation({
    mutationFn: (documentId: number) => api.post(`/document/${documentId}/mark-processed`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });
  const [tabKey, setTabKey] = useState(tabs?.[0]?.key ?? "");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);

  const activeTab = tabs?.find((t) => t.key === tabKey) ?? tabs?.[0];
  const activeFetcher = activeTab?.fetcher ?? fetcher;
  if (!activeFetcher) throw new Error("DocumentViewListPage cần `fetcher` hoặc `tabs`");

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, tabKey, page, pageSize, applied],
    queryFn: () =>
      activeFetcher({
        page,
        pageSize: pageSize,
        q: applied.q || undefined,
        fromDate: applied.fromDate || undefined,
        toDate: applied.toDate || undefined,
        typeId: applied.typeId || undefined,
      }),
  });

  const columns: Column<DocumentSummary>[] = [
    {
      key: "stt",
      header: "STT",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "code",
      header: "Mã văn bản",
      width: "180px",
      render: (r) => <span className="font-mono text-xs">{r.registerNumber ?? r.code ?? "—"}</span>,
    },
    {
      key: "title",
      header: "Trích yếu nội dung",
      // Hệ cũ phân biệt ngay trên dòng: chưa đọc in đậm, đã bị thu hồi gạch ngang.
      render: (r) => (
        <span
          className={cn(
            r.isRecalled ? "line-through text-muted-foreground" : "",
            r.isRead === false ? "font-semibold" : "font-medium",
          )}
        >
          {r.title ?? "—"}
        </span>
      ),
    },
    // Bốn cột dưới đây hệ cũ có trên lưới "Văn bản cá nhân"; giữ nguyên thứ tự
    // của hệ cũ: Người gửi → Người ký → Trạng thái xử lý → Nội dung xử lý.
    { key: "sender", header: "Người gửi", width: "180px", render: (r) => r.senderName ?? "—" },
    {
      key: "signer",
      header: "Người ký",
      width: "180px",
      render: (r) => <span>{r.signer ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Trạng thái xử lý",
      width: "140px",
      render: (r) => <span className="tabular-nums">{r.statusNumber ?? "—"}</span>,
    },
    {
      key: "processContent",
      header: "Nội dung xử lý",
      width: "220px",
      render: (r) => r.processContent ?? "—",
    },
    {
      key: "promulgate",
      header: "Ngày ban hành",
      width: "130px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.promulgateDate?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "mainFile",
      header: "File văn bản",
      width: "220px",
      render: (r) => (
        <AttachmentCell files={r.mainFile ? [r.mainFile] : null} onOpen={setPreviewFile} />
      ),
    },
  ];
  columns.splice(1, 0, {
    key: "action",
    header: "Thao tác",
    width: "120px",
    align: "center",
    render: (r) => (
      <RowActions
        actions={[
          { label: "Xem chi tiết", icon: Eye, onClick: () => nav(`/documents/${r.documentId}`) },
          { label: "Chuyển văn bản", icon: Send, onClick: () => setTransferDoc(r) },
          {
            label: "Đánh dấu đã xử lý",
            icon: CheckCheck,
            disabled: markMut.isPending,
            onClick: () => markMut.mutate(r.documentId),
          },
        ]}
      />
    ),
  });

  const submit = () => {
    setApplied(filters);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} văn bản`}
        breadcrumb={breadcrumb}
      />
      {tabs && tabs.length > 1 && (
        <div role="tablist" className="flex gap-1 mb-3 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={t.key === (activeTab?.key ?? "")}
              type="button"
              onClick={() => {
                setTabKey(t.key);
                setPage(1);
              }}
              className={
                t.key === (activeTab?.key ?? "")
                  ? "px-4 h-9 text-sm font-medium border-b-2 border-primary text-primary-700"
                  : "px-4 h-9 text-sm text-muted-foreground border-b-2 border-transparent hover:text-foreground"
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <SearchPanel
        fields={[
          {
            label: "Từ khóa",
            control: (
              <input
                className={filterInputClass}
                placeholder="Tiêu đề, mã văn bản…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            ),
          },
          {
            label: "Thể thức",
            control: (
              <input
                className={filterInputClass}
                placeholder="ID thể thức (TYPE_ID)"
                value={filters.typeId}
                onChange={(e) => setFilters((f) => ({ ...f, typeId: e.target.value }))}
                inputMode="numeric"
              />
            ),
          },
          {
            label: "Từ ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.fromDate}
                onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              />
            ),
          },
          {
            label: "Đến ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.toDate}
                onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              />
            ),
          },
        ]}
        onSubmit={submit}
      />
      <div className="mb-2 flex flex-wrap items-center justify-end gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary" /> Văn bản chưa đọc
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground/40" /> Văn bản đã đọc
        </span>
        <span className="inline-flex items-center gap-1.5 line-through">
          <span className="h-2.5 w-2.5 rounded-sm bg-destructive" /> Văn bản bị thu hồi
        </span>
      </div>
      <DataTable<DocumentSummary>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r, i) => `${r.documentId}-${i}`}
        isLoading={isLoading}
        onRowClick={(r) => nav(`/documents/${r.documentId}`)}
        empty={
          <>
            <FileText className="inline h-4 w-4 mr-1" /> Không có văn bản
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
      <TransferDialog
        doc={transferDoc}
        onClose={() => setTransferDoc(null)}
        onDone={() => {
          setTransferDoc(null);
          qc.invalidateQueries({ queryKey: [queryKey] });
        }}
      />
    </div>
  );
}

/**
 * Hộp thoại "Chuyển văn bản" — chọn người nhận rồi tạo phiếu nhận mới cho họ,
 * đúng như hành động cùng tên trên lưới Văn bản cá nhân của hệ cũ.
 */
function TransferDialog({
  doc,
  onClose,
  onDone,
}: {
  doc: DocumentSummary | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [picked, setPicked] = useState<PickedEntity[]>([]);
  const [current, setCurrent] = useState<PickedEntity | null>(null);
  const [comment, setComment] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/document/${doc!.documentId}/transfer`, {
        receiverIds: picked.map((p) => p.id),
        comment: comment || undefined,
      }),
    onSuccess: () => {
      setPicked([]);
      setComment("");
      onDone();
    },
  });

  return (
    <Modal
      open={doc !== null}
      onClose={onClose}
      title={`Chuyển văn bản: ${doc?.title ?? ""}`}
      size="md"
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose}>
            Đóng
          </Button>
          <Button
            variant="default"
            size="md"
            disabled={picked.length === 0 || mut.isPending}
            onClick={() => mut.mutate()}
          >
            Chuyển {picked.length > 0 ? `(${picked.length})` : ""}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <div className="text-sm text-muted-foreground mb-1.5">Thêm người nhận</div>
          <EntityPickerField
            kind="staff"
            value={current}
            onChange={(v) => {
              if (v && !picked.some((p) => p.id === v.id)) setPicked((l) => [...l, v]);
              setCurrent(null);
            }}
          />
        </div>
        {picked.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {picked.map((p) => (
              <li
                key={p.id}
                className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-sm border border-border text-xs"
              >
                {p.name}
                <button
                  type="button"
                  aria-label={`Bỏ ${p.name}`}
                  className="h-5 w-5 grid place-items-center rounded-sm hover:bg-muted text-destructive"
                  onClick={() => setPicked((l) => l.filter((x) => x.id !== p.id))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <div className="text-sm text-muted-foreground mb-1.5">Ý kiến chuyển</div>
          <input
            className={filterInputClass}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        {mut.isError && (
          <div className="text-sm text-destructive">Chuyển văn bản không thành công.</div>
        )}
      </div>
    </Modal>
  );
}
